<template>
  <div class="row pb-2 mb-2 px-3">
    <b-button variant="primary" @click="openEmployeeList">Leave Balance From Contract Range</b-button>

    <!-- Modal 1: Employee List Selection Modal -->
    <b-modal id="bv-employee-list-modal" size="xl" title="Select Employee" hide-footer scrollable>
      <div class="p-3">
        <b-overlay :show="loading_list" rounded="sm">
          <common-list
            v-if="employees.length > 0"
            :checkbox="checkbox"
            :is_action="is_action"
            :key="list_key"
            :appended_column="appended_column"
            :company_options="company_options"
            :employees="employees"
            :default_column="default_column"
            :primary_key="primary_key"
            :all_employee="all_employee"
            v-on:childToParent="listReturn"
          ></common-list>
          <div v-else-if="!loading_list" class="text-center py-5 text-muted">
            No employee records found.
          </div>
        </b-overlay>
      </div>
    </b-modal>

    <!-- Modal 2: Date Picker & Leave Balance Table -->
    <b-modal id="bv-balance-report-modal" size="lg" title="Dynamic Leave Balance Report" hide-footer scrollable @hidden="onModalHidden">
      <div class="p-3">
        <!-- Selected Employee Details -->
        <div class="mb-4 d-flex align-items-center p-3 bg-light rounded" v-if="selected_employee">
          <b-avatar variant="primary" class="mr-3" :text="getInitials(selected_employee.name)"></b-avatar>
          <div>
            <h5 class="mb-1">{{ selected_employee.name }}</h5>
            <div class="text-muted small">
              <strong>Code:</strong> {{ selected_employee.employee_code }} | 
              <strong>Dept:</strong> {{ selected_employee.department }} | 
              <strong>Role:</strong> {{ selected_employee.jobposition }}
            </div>
          </div>
        </div>

        <!-- Date Range Selection -->
        <div class="row mb-4">
          <div class="col-md-6">
            <label class="font-weight-bold text-secondary">Contract Start Date</label>
            <b-form-datepicker id="contract-start" v-model="contract_start" @input="fetchLeaveBalance" class="mb-2" placeholder="Choose a date"></b-form-datepicker>
          </div>
          <div class="col-md-6">
            <label class="font-weight-bold text-secondary">Contract End Date</label>
            <b-form-datepicker id="contract-end" v-model="contract_end" @input="fetchLeaveBalance" class="mb-2" placeholder="Choose a date"></b-form-datepicker>
          </div>
        </div>

        <!-- Leave Balance Details Table -->
        <b-overlay :show="loading_balance" rounded="sm">
          <div v-if="leave_available.length > 0">
            <h5 class="mb-3 border-bottom pb-2 text-secondary">Leave Summary Table</h5>
            <div class="table-responsive">
              <table class="table dataTable">
                <thead>
                  <tr class="table-head">
                    <th>Type</th>
                    <th>Leaves Allowed</th>
                    <th>Carried</th>
                    <th>Applied</th>
                    <th>Enjoyed</th>
                    <th>Encashed</th>
                    <th>Deducted</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="table-row hover-bg" v-for="leave_type in leave_available" :key="leave_type.id">
                    <td class="font-weight-bold">{{ leave_type.leave_title }}</td>
                    <td>
                      {{ leave_type.allowed }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                    <td>
                      {{ leave_type.carried }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                    <td>
                      {{ leave_type.applied }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                    <td>
                      {{ leave_type.enjoyed }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                    <td>
                      {{ leave_type.encashed }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                    <td>
                      {{ leave_type.deducted }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                    <td class="font-weight-bold text-success">
                      {{ leave_type.remaining }}
                      <span v-if="is_roster">Shifts</span>
                      <span v-else>Days</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else-if="!contract_start || !contract_end" class="text-center text-muted py-5 border rounded bg-light col-12">
            <b-icon icon="calendar-range" scale="2" class="mb-3 text-secondary"></b-icon>
            <p class="mb-0">Please select both Contract Start Date and Contract End Date to fetch leave balance summary.</p>
          </div>
          <div v-else class="text-center text-muted py-5 border rounded bg-light col-12">
            <b-icon icon="info-circle" scale="2" class="mb-3 text-secondary"></b-icon>
            <p class="mb-0">No leave records or policy details found for the selected date range.</p>
          </div>
        </b-overlay>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      employees: [],
      company_options: [],
      loading_list: false,
      loading_balance: false,
      selected_employee: null,
      contract_start: '',
      contract_end: '',
      leave_available: [],
      is_roster: false,

      // common-list props
      checkbox: 0,
      is_action: 0,
      list_key: 0,
      default_column: ['name', 'employee_code', 'department', 'jobposition', 'branch'],
      primary_key: 'employee_id',
      appended_column: [],
      all_employee: 'yes'
    };
  },

  methods: {
    openEmployeeList() {
      this.$bvModal.show('bv-employee-list-modal');
      if (this.employees.length === 0) {
        this.getEmployees();
      }
    },

    getEmployees() {
      this.loading_list = true;
      axios.get(this.$api_url + '/employee/list')
        .then(response => {
          this.loading_list = false;
          if (response.data.error == 1) {
            console.error(response.data.notice);
          } else {
            this.employees = response.data.employee_data || [];
            this.company_options = response.data.options || [];
            this.list_key += 1;
          }
        })
        .catch(error => {
          this.loading_list = false;
          if (typeof this.serverError === 'function') {
            this.serverError(error);
          } else {
            console.error(error);
          }
        });
    },

    listReturn(data) {
      if (data && data.employee_id > 0) {
        const emp = this.employees.find(e => e.employee_id == data.employee_id);
        if (emp) {
          this.$bvModal.hide('bv-employee-list-modal');
          this.selectEmployee(emp);
        }
      }
    },

    selectEmployee(employee) {
      this.selected_employee = employee;
      this.contract_start = '';
      this.contract_end = '';
      this.leave_available = [];

      let formData = new FormData();
      formData.append('employee_id', employee.employee_id);

      axios.post(this.$api_url + '/employee/setemployee', formData)
        .then(response => {
          this.$bvModal.show('bv-balance-report-modal');
        })
        .catch(error => {
          if (typeof this.serverError === 'function') {
            this.serverError(error);
          } else {
            console.error(error);
          }
        });
    },

    fetchLeaveBalance() {
      if (!this.contract_start || !this.contract_end) {
        return;
      }

      this.loading_balance = true;
      axios.post(this.$api_url + '/leave-balance-for-contract-range', {
        employee_id: this.selected_employee.employee_id,
        contract_start: this.contract_start,
        contract_end: this.contract_end
      })
      .then(response => {
        if (response.data && response.data.success) {
          this.leave_available = response.data.available || [];
        } else {
          this.leave_available = [];
        }
        this.loading_balance = false;
      })
      .catch(error => {
        this.loading_balance = false;
        if (typeof this.serverError === 'function') {
          this.serverError(error);
          console.error(error);
        } else {
          console.error(error);
        }
      });
    },

    getInitials(name) {
      if (!name) return 'EE';
      const parts = name.split(' ');
      return parts.map(p => p[0]).join('').substring(0, 2).toUpperCase();
    },

    onModalHidden() {
      this.selected_employee = null;
      this.contract_start = '';
      this.contract_end = '';
      this.leave_available = [];
    }
  }
};
</script>

<style scoped>
.employee-avatar {
  border-radius: 50%;
}
</style>